// @flow
import React, {useEffect, useState} from 'react';
import type {Node} from 'react';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type {FormType} from '@wellstone-solutions/common';

import {
  Box,
  Form as CommonForm,
  FormRow,
  Select,
  Stack,
  TextField,
} from '@wellstone-solutions/web';
import {useStores} from 'hooks/useStores';
import type {Group} from '../../types';
import {ChatSwitch} from './ChatSwitch';
import {MAX_GROUP_DESC_LENGTH, MAX_GROUP_NAME_LENGTH} from '../../constants';
import {CountdownHelperText} from 'components/forms';

type PropsType = {
  formId: string,
  form: FormType<Group>,
};

export const Form = ({form, formId}: PropsType): Node => {
  useEffect(() => {
    setNameCountdown(MAX_GROUP_NAME_LENGTH - form.values.name.length);
    setDescCountdown(MAX_GROUP_DESC_LENGTH - form.values.description.length);
  }, [form.values]);

  const [nameCountdown, setNameCountdown] = useState(
    MAX_GROUP_NAME_LENGTH - form.values.name.length,
  );
  const [descCountdown, setDescCountdown] = useState(MAX_GROUP_DESC_LENGTH); // description
  const {meStore, organizationStore} = useStores();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {groupCategories} = organizationStore;

  const programItems = meStore.myPrograms.map((program) => ({
    label: program.name,
    value: program.id,
  }));

  const groupTypeItems = groupCategories.map((category) => ({
    label: category.name,
    value: category.id,
  }));

  const handleNameChange = (event) => {
    const value = event.target.value;

    form.setFieldTouched('name');
    form.setFieldValue('name', value);
  };

  const handleDescChange = (event) => {
    const value = event.target.value;

    form.setFieldTouched('description');
    form.setFieldValue('description', value);
  };

  const handleProgramChange = (event) => {
    const value = event.target.value;

    const [selectedValue] = programItems.filter(
      (program) => program.value === value,
    );

    const newValue = value.length > 0 ? [selectedValue.value] : [];

    form.setFieldTouched('programs');
    form.setFieldValue('programs', newValue);
  };

  const handleGroupTypeChange = (event) => {
    const value = event.target.value;

    const [selectedCategory] = groupTypeItems.filter(
      (category) => (category.value = value),
    );

    form.setFieldTouched('category');
    form.setFieldValue('category', selectedCategory?.value ?? '');
  };

  return (
    <CommonForm
      data-testid="add-group-form"
      id={formId}
      onSubmit={form.handleSubmit}>
      <Stack
        direction="column"
        sx={{
          width: {
            xs: '100%',
            lg: '75%',
          },
        }}>
        <FormRow>
          <TextField
            error={Boolean(form.touched.name && form.errors.name)}
            formField="name"
            label="Group Name"
            inputProps={{maxLength: MAX_GROUP_NAME_LENGTH + 1}}
            onBlur={() => {
              form.setFieldTouched('name');
            }}
            onChange={handleNameChange}
            value={form.values.name}
            FormHelperTextProps={{
              component: 'div',
            }}
            helperText={
              <CountdownHelperText
                helperText={form.touched.name && form.errors.name}
                countdown={nameCountdown}
              />
            }
            sx={{mb: 1}}
          />
        </FormRow>
        <FormRow>
          <TextField
            multiline
            inputProps={{maxLength: MAX_GROUP_DESC_LENGTH + 1}}
            error={Boolean(
              form.touched.description && Boolean(form.errors.description),
            )}
            formField="description"
            label="Description (Optional)"
            onChange={handleDescChange}
            value={form.values.description}
            FormHelperTextProps={{
              component: 'div',
            }}
            helperText={
              <CountdownHelperText
                helperText={
                  !isMobile
                    ? 'Describe the activities or services this group provides to its members.'
                    : ''
                }
                countdown={descCountdown}
              />
            }
            sx={{mb: 2}}
          />
        </FormRow>

        <FormRow>
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={3}>
            <Box sx={{flex: 1}}>
              <Select
                canUnselect={false}
                containerProps={{
                  fullWidth: true,
                }}
                label="Program"
                value={form.values.programs[0] ?? ''}
                onChange={handleProgramChange}
                items={programItems}
              />
            </Box>
            <Box sx={{flex: 1}}>
              <Select
                canUnselect={false}
                containerProps={{
                  fullWidth: true,
                }}
                label="Group Type"
                value={form.values.category}
                onChange={handleGroupTypeChange}
                items={groupTypeItems}
              />
            </Box>
          </Stack>
        </FormRow>
        <FormRow>
          <ChatSwitch form={form} />
        </FormRow>
      </Stack>
    </CommonForm>
  );
};
